/**
 * We have two main ways to specify a chord indepently of the instrument:
 *
 * a) <name, variant> - e.g., Cm, C7, Cm7, C7sus4, etc.
 * b) [notes] - the notes that make up the chord
 *
 * In addition to this, we may have additional information that are specific to an instrument, e.g.,
 *  - fingering on the guitar and/or bass, keyboard and so on
 */

import type { NoteName } from '@tunasong/schemas'

export interface ChordVariant {
  root: NoteName
  variant: ChordType
  // Slash chords may have a bass note, e.g., C/E
  bassNote?: NoteName
}

export interface ChordNotes {
  name: string
  notes: NoteName[]
}

export type Chord = ChordVariant | ChordNotes

export function isChordVariant(node: unknown): node is ChordVariant {
  /**
   * `variant` must be a real string — `''` (the major triad) is valid, which is
   * why this can't be a simple truthiness check. The previous
   * `typeof variant !== 'undefined'` accidentally accepted `variant: null`
   * (and other non-strings) found in some persisted documents; those then
   * crashed every consumer that runs string methods on `chord.variant`
   * (GH #1510, `chordalDissonance`'s `variant.startsWith`).
   */
  return Boolean((node as ChordVariant)?.root) && typeof (node as ChordVariant)?.variant === 'string'
}

export function isChordNotes(node: unknown): node is ChordNotes {
  return Boolean(node && typeof node === 'object' && 'notes' in node && Array.isArray(node.notes))
}

type ChordSpec = Record<string, { shortName: string; intervals: string[]; aliases?: string[] }>

const triads: ChordSpec = {
  '': {
    shortName: '',
    intervals: ['I', 'III', 'V'],
    aliases: ['M'],
  },

  m: {
    shortName: 'm',
    intervals: ['I', 'bIII', 'V'],
  },
  aug: {
    shortName: 'aug',
    intervals: ['I', 'III', '#V'],
  },
  dim: {
    shortName: 'dim',
    intervals: ['I', 'bIII', 'bV'],
  },
  sus2: {
    shortName: 'sus2',
    intervals: ['I', 'II', 'V'],
  },
  sus4: {
    shortName: 'sus4',
    intervals: ['I', 'IV', 'V'],
  },
  sus2sus4: {
    shortName: 'sus2',
    intervals: ['I', 'II', 'IV', 'V'],
  },
  /** Power chord */
  '5': {
    shortName: '5',
    intervals: ['I', 'V'],
  },
  /** Half-diminished (m7b5) - very common in jazz */
  m7b5: {
    shortName: 'm7b5',
    intervals: ['I', 'bIII', 'bV', 'bVII'],
    aliases: ['ø', 'ø7', 'min7b5'],
  },
  /** Dominant 7 flat 5 */
  '7b5': {
    shortName: '7b5',
    intervals: ['I', 'III', 'bV', 'bVII'],
  },
  /** Dominant 7 sharp 5 (augmented 7) */
  '7#5': {
    shortName: '7#5',
    intervals: ['I', 'III', '#V', 'bVII'],
    aliases: ['aug7', '+7'],
  },
  /** Dominant 7 sharp 9 (Hendrix chord) */
  '7#9': {
    shortName: '7#9',
    intervals: ['I', 'III', 'V', 'bVII', '#II'],
  },
  /** Dominant 7 flat 9 */
  '7b9': {
    shortName: '7b9',
    intervals: ['I', 'III', 'V', 'bVII', 'bII'],
  },
}

/** @todo we can probably generate these */
const elements: ChordSpec = {
  '6': {
    shortName: '6',
    intervals: ['VI'],
  },
  '7': {
    shortName: '7',
    intervals: ['bVII'],
  },
  /** Contains all the add intervals - i.e., 3, 5 and a flat 7th */
  '9': {
    shortName: '9',
    intervals: ['II', 'bVII'],
  },
  maj7: {
    shortName: 'maj7',
    intervals: ['VII'],
    aliases: ['Δ', 'M7'],
  },
  add9: {
    shortName: 'add9',
    intervals: ['II'],
  },
  add11: {
    shortName: 'add9',
    intervals: ['IV'],
  },
}

const types = { ...triads, ...elements }

export type NoteElement = keyof typeof elements
export type ChordElement = keyof typeof types
export type TriadType = keyof typeof triads

export const ALL_CHORD_TYPES = types
export const ALL_CHORD_TRIADS = triads
export const ALL_CHORD_TRIAD_TYPES = Object.keys(triads) as TriadType[]
export const ALL_CHORD_ELEMENTS = elements
export const ALL_CHORD_ELEMENT_TYPES = Object.keys(elements) as NoteElement[]

/** @todo should be managed automatically, but difficult because we compose from the different types */
export type ChordType =
  | ''
  | 'M'
  | 'm'
  | 'aug'
  | 'dim'
  | 'sus2'
  | 'sus4'
  | 'sus2sus4'
  | '7sus2'
  | '7sus4'
  | '7sus2sus4'
  | '5'
  | 'b5'
  | 'b6'
  | '6'
  | '7'
  | '9'
  | 'maj7'
  | 'Δ' // alias for maj7
  | 'M7'
  /* Minors */
  | 'mb5'
  | 'mb6'
  | 'm6'
  | 'm7'
  | 'mmaj7'
  /* aug */
  | 'augb5'
  | 'augb6'
  | 'aug6'
  | 'aug7'
  | 'augmaj7'
  /* dim */
  | 'dimb5'
  | 'dimb6'
  | 'dim6'
  | 'dim7'
  | 'dimmaj7'
  /* Altered chords */
  | 'm7b5' // half-diminished
  | 'ø' // half-diminished alias
  | 'ø7' // half-diminished alias
  | 'min7b5' // half-diminished alias
  | '7b5'
  | '7#5'
  | '+7' // aug7 alias
  | '7#9'
  | '7b9'
